import { useEffect, useRef, useState } from 'react'
import { useThemeMode } from '_metronic/partials'
import ApexCharts, { ApexOptions } from 'apexcharts'

import { extractBarChart } from 'Utils/ApexChartsTooltipsExtractration'
import { FREE_COLOR, PLUS_COLOR, PRO_COLOR } from 'Configuration/Colors/Graph'
import moment from 'moment'

const ViewModel = () => {
	const chartRef = useRef<HTMLDivElement | null>(null)
	const { mode } = useThemeMode()
	const [currentDataType, setCurrentDataType] = useState<'order' | 'sale' | 'bought'>('order')

	const getChartOptions = (): ApexOptions => {
		const tempData = [
			{
				month: '2023-08',
				free: 100,
				packagePlus: 0,
				packagePro: 0,
			},
			{
				month: '2023-09',
				free: 200,
				packagePlus: 20,
				packagePro: 0,
			},
			{
				month: '2023-10',
				free: 300,
				packagePlus: 50,
				packagePro: 10,
			},
			{
				month: '2023-11',
				free: 400,
				packagePlus: 100,
				packagePro: 20,
			},
			{
				month: '2023-12',
				free: 500,
				packagePlus: 150,
				packagePro: 30,
			},
			{
				month: '2024-01',
				free: 600,
				packagePlus: 200,
				packagePro: 40,
			},
			{
				month: '2024-02',
				free: 700,
				packagePlus: 250,
				packagePro: 50,
			},
			{
				month: '2024-03',
				free: 800,
				packagePlus: 300,
				packagePro: 60,
			},
			{
				month: '2024-04',
				free: 900,
				packagePlus: 350,
				packagePro: 70,
			},
			{
				month: '2024-05',
				free: 1000,
				packagePlus: 400,
				packagePro: 80,
			},
			{
				month: '2024-06',
				free: 1100,
				packagePlus: 450,
				packagePro: 90,
			},
			{
				month: '2024-07',
				free: 1200,
				packagePlus: 500,
				packagePro: 100,
			},
		]

		const freeDatas = tempData?.map((d: any) => d.free) ?? []
		const plusDatas = tempData?.map((d: any) => d.packagePlus) ?? []

		return {
			subtitle: {
				text: 'อัตราการซื้อสินค้า',
				margin: 0,
				style: {
					fontSize: '18px',
					fontWeight: 'bold',
					fontFamily: 'Noto Sans Thai, sans-serif',
				},
			},
			grid: {
				padding: {
					top: 45,
				},
			},
			series: [
				{
					name: 'ลูกค้าใหม่',
					data: freeDatas,
				},
				{
					name: 'ลูกค้าสั่งซ้ำ',
					data: plusDatas,
				},
				// {
				// 	name: 'Pro',
				// 	data: proDatas,
				// },
			],
			colors: [FREE_COLOR, PLUS_COLOR, PRO_COLOR],
			dataLabels: {
				enabled: false,
			},
			chart: {
				type: 'bar',
				height: 350,
				stacked: true,
				toolbar: {
					show: false,
				},
			},
			responsive: [
				{
					breakpoint: 480,
					options: {
						legend: {
							position: 'top',
							horizontalAlign: 'left',
						},
					},
				},
				{
					breakpoint: 481,
					options: {
						legend: {
							position: 'bottom',
							offsetX: 10,
							offsetY: 0,
						},
					},
				},
			],
			plotOptions: {
				bar: {
					horizontal: false,
					borderRadius: 8,
					dataLabels: {},
				},
			},
			xaxis: {
				type: 'category',
				categories: (tempData ?? []).map((d: any) => moment(d.month, 'YYYY-MM').format('MMM YYYY')),
			},
			legend: {
				position: 'top',
				horizontalAlign: 'left',
				floating: true,
				offsetY: 0,
				markers: {
					width: 12,
					height: 12,
					radius: 6,
				},
				fontFamily: 'Noto Sans Thai, sans-serif',
				onItemHover: {
					highlightDataSeries: true,
				},
			},
			fill: {
				opacity: 1,
			},
			tooltip: {
				custom: function ({ w, dataPointIndex }) {
					const datas = w.globals.initialSeries
					const dataColor = w.globals.colors

					const extractedDatas = extractBarChart(datas, dataColor, dataPointIndex)
					const total = extractedDatas.reduce((acc, cur) => acc + cur.total, 0)

					return `<div class="bg-kumopack-base-black pt-2 pb-5 px-5 text-kumopack-base-white">
                                <div class="fw-bold">${total} Users</div>
                                ${extractedDatas
																	.map(
																		d => `
                                    <div class="d-flex align-items-center justify-content-between">
                                        <div class="rounded-circle" style="width:9px;height:9px;background-color:${d.color}"></div>
                                        <div class="ms-1">${d.total}</div>
                                        <div class="ms-3">users</div>
                                    </div>
                            `
																	)
																	.join(``)}
                            </div>`
				},
			},
		}
	}

	const refreshChart = () => {
		if (!chartRef.current) {
			return
		}

		const chart = new ApexCharts(chartRef.current, getChartOptions())
		if (chart) {
			chart.render()
		}

		return chart
	}

	useEffect(() => {
		const chart = refreshChart()

		return () => {
			if (chart) {
				chart.destroy()
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chartRef, mode])

	return {
		chartRef,
		currentDataType,
		setCurrentDataType,
	}
}

export default ViewModel
